import { useQueryClient } from '@tanstack/react-query';
import type { TranslationFillResult } from '@testflow/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from './api.js';
import { catalogApi } from './catalog-api.js';
import { invalidateCatalogTestCase } from './catalog-cache.js';
import { mapApiError } from './errors.js';
import { toast } from './toast.js';

// #111: Sammellauf „Alle N ergänzen". Ruft den Einzel-Endpunkt strikt nacheinander je Testfall auf
// (Modus 'bulk') — bewusst kein Parallelbetrieb: das KI-Ratenlimit ist der Engpass, und ein
// Abbruch soll nach spätestens einem laufenden Aufruf wirken. Der Fortschritt liegt hier im Hook,
// nicht im Server, weil ein Sammellauf über Hunderte Testfälle sonst eine Anfrage von Minuten wäre.

export interface FillTarget {
  test_case_id: string;
  human_id: string;
}

export type FillRunStatus = 'idle' | 'running' | 'done' | 'cancelled';

// #111: Warum der Lauf vorzeitig endete. Alles außer `forbidden` kommt aus `failure` im Antwortkörper
// des Servers (503 = dauerhaft: weitere Aufrufe der Schleife scheitern identisch).
export type FillStopReason = 'not_configured' | 'cost_budget' | 'structural' | 'forbidden';
const PERMANENT_FAILURES: readonly string[] = ['not_configured', 'cost_budget', 'structural'];

export const RETRY_DELAY_MS = 5000;

// #160: Der Server bricht eine Ergänzung nach 25 s mit `time_budget` ab (Hub-Timeout 30 s), auch wenn
// er schon Felder geschrieben hat. Dann wird derselbe Testfall erneut angefragt — höchstens so oft,
// damit ein Testfall, der nie fertig wird, den Sammellauf nicht endlos aufhält.
export const FILL_MAX_ROUNDS = 3;

// Vorübergehende Serverfehler: Ratenlimit, KI-Fehler, nicht erreichbar, Gateway-Timeout.
const RETRYABLE = new Set([429, 502, 503, 504]);

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// 'unchanged' = Server antwortete ohne Fehler, schrieb aber nichts und die Lücke bleibt (Text in beiden
// Sprachen gleich, z. B. Eigenname) — weder ergänzt noch fehlgeschlagen. 'stop' beendet den Lauf.
// #160: 'partial' = nach einem Teilerfolg (`time_budget`, Felder geschrieben) bleibt auch nach den
// Wiederholungen eine Lücke — etwas ist ergänzt, also nicht „fehlgeschlagen".
type Outcome =
  | { kind: 'ok' | 'unchanged' | 'failed' | 'partial' }
  | { kind: 'stop'; reason: FillStopReason };
// Ein einzelner Aufruf (samt Wiederholversuch bei vorübergehendem Fehler): Antwort oder Ausgang.
type Reply =
  | { kind: 'reply'; res: TranslationFillResult }
  | { kind: 'failed' }
  | { kind: 'stop'; reason: FillStopReason };

// Der Körper einer Fehlerantwort ist das TranslationFillResult als JSON; jeder Parse-Fehler heißt
// „Ursache unbekannt" (null) und lässt den Aufruf transient behandeln. Auch vom Einzelknopf genutzt.
export function translationFillFailureOf(err: ApiError): string | null {
  try {
    const body: unknown = JSON.parse(err.message);
    const failure = (body as { failure?: unknown } | null)?.failure;
    return typeof failure === 'string' ? failure : null;
  } catch {
    return null; // kein JSON (z. B. Proxy-Fehlerseite)
  }
}

function permanentFailureOf(err: ApiError): FillStopReason | null {
  const failure = translationFillFailureOf(err);
  return failure !== null && PERMANENT_FAILURES.includes(failure)
    ? (failure as FillStopReason)
    : null;
}

export function useTranslationFillRun(
  projectId: string,
  deps: { sleep?: (ms: number) => Promise<void> } = {},
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sleep = deps.sleep ?? defaultSleep;

  const [status, setStatus] = useState<FillRunStatus>('idle');
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [succeeded, setSucceeded] = useState(0);
  const [unchanged, setUnchanged] = useState<FillTarget[]>([]);
  const [partial, setPartial] = useState<FillTarget[]>([]);
  const [failed, setFailed] = useState<FillTarget[]>([]);
  const [stopReason, setStopReason] = useState<FillStopReason | null>(null);

  // Refs statt State: der laufende Schleifendurchlauf muss den Abbruch sofort sehen, nicht erst
  // nach dem nächsten Render.
  const cancelled = useRef(false);
  const running = useRef(false);
  // Unmount (Dialog zu, Seitenwechsel) wirkt wie Abbruch — es soll kein Lauf ohne Anzeige weiterlaufen.
  useEffect(
    () => () => {
      cancelled.current = true;
    },
    [],
  );

  const requestOne = useCallback(
    async (target: FillTarget): Promise<Reply> => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const res = await catalogApi.fillTranslations(projectId, target.test_case_id, 'bulk');
          return { kind: 'reply', res };
        } catch (err) {
          if (err instanceof ApiError && err.status === 403) {
            toast.error(mapApiError(t, err));
            return { kind: 'stop', reason: 'forbidden' };
          }
          // Dauerhafter Grund (KI nicht eingerichtet, Kostenbudget, Konfigurationsfehler): ein Retry
          // und jeder weitere Testfall scheitert identisch → sofort beenden und den Grund nennen.
          if (err instanceof ApiError) {
            const reason = permanentFailureOf(err);
            if (reason) return { kind: 'stop', reason };
          }
          const retryable = !(err instanceof ApiError) || RETRYABLE.has(err.status);
          if (!retryable || attempt === 1) return { kind: 'failed' };
          await sleep(RETRY_DELAY_MS);
          // Während der Wartezeit abgebrochen → keinen weiteren Aufruf mehr starten.
          if (cancelled.current) return { kind: 'failed' };
        }
      }
      return { kind: 'failed' };
    },
    [projectId, sleep, t],
  );

  const fillOne = useCallback(
    async (target: FillTarget): Promise<Outcome> => {
      // #160: Gab es in einer früheren Runde schon einen Teilerfolg, ist der Testfall nicht mehr
      // „fehlgeschlagen" oder „unverändert", sondern teilweise ergänzt.
      let partialBefore = false;
      for (let round = 1; round <= FILL_MAX_ROUNDS; round += 1) {
        const reply = await requestOne(target);
        if (reply.kind === 'stop') return reply;
        if (reply.kind === 'failed') return partialBefore ? { kind: 'partial' } : reply;
        const { res } = reply;
        // #160: Teilerfolg — derselbe Testfall noch einmal, außer der Lauf wurde abgebrochen.
        if (res.failure === 'time_budget' && res.filled > 0) {
          partialBefore = true;
          if (cancelled.current) break;
          continue;
        }
        // 200 mit Fehlerklasse = nichts geschrieben oder anderer Fehler → nicht als ergänzt zählen.
        // Kein Wiederholversuch: der Server hat dann schon seine eigenen Versuche gemacht.
        if (res.failure) return partialBefore ? { kind: 'partial' } : { kind: 'failed' };
        // Kein Fehler, nichts geschrieben, Lücke besteht weiter: die KI hat den Text unverändert
        // zurückgegeben — das ist kein Erfolg, sonst meldet „Alle N ergänzen" N Treffer ohne Wirkung.
        if (res.filled === 0 && res.remaining_gap) {
          return partialBefore ? { kind: 'partial' } : { kind: 'unchanged' };
        }
        return { kind: 'ok' };
      }
      // Obergrenze erreicht oder abgebrochen — nach einem Teilerfolg bleibt die Lücke.
      return { kind: 'partial' };
    },
    [requestOne],
  );

  const start = useCallback(
    (targets: FillTarget[]) => {
      if (running.current) return;
      running.current = true;
      cancelled.current = false;
      setStatus('running');
      setDone(0);
      setTotal(targets.length);
      setSucceeded(0);
      setUnchanged([]);
      setPartial([]);
      setFailed([]);
      setStopReason(null);

      void (async () => {
        let processed = 0;
        let finished = true;
        for (const target of targets) {
          if (cancelled.current) {
            finished = false;
            break;
          }
          // #160: Wiederholungen eines Testfalls laufen in fillOne — der Fortschritt zählt Testfälle,
          // nicht Aufrufe, und bleibt damit bei höchstens 100 %.
          const outcome = await fillOne(target);
          processed += 1;
          setDone(processed);
          if (outcome.kind === 'ok') setSucceeded((n) => n + 1);
          else if (outcome.kind === 'unchanged') setUnchanged((list) => [...list, target]);
          else if (outcome.kind === 'partial') setPartial((list) => [...list, target]);
          else setFailed((list) => [...list, target]);
          // Fehlendes Recht oder dauerhafter KI-Fehler gilt für jeden weiteren Aufruf gleich — Lauf
          // beenden statt N× denselben Fehler. Der aktuelle Testfall zählt oben als fehlgeschlagen.
          if (outcome.kind === 'stop') {
            setStopReason(outcome.reason);
            break;
          }
        }
        // Abbruch erst nach dem letzten Ziel ist ein normales Ende (finished bleibt true).
        running.current = false;
        // Auch nach Abbruch/Teilerfolg wurden Felder geschrieben → Caches immer neu laden.
        invalidateCatalogTestCase(queryClient, projectId);
        setStatus(finished ? 'done' : 'cancelled');
      })();
    },
    [fillOne, queryClient, projectId],
  );

  const cancel = useCallback(() => {
    cancelled.current = true;
  }, []);

  return { status, done, total, succeeded, unchanged, partial, failed, stopReason, start, cancel };
}
